import {Box, HStack, StackProps, Text, VStack} from '@chakra-ui/react';
import {EditIcon} from '@chakra-ui/icons';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {toDecimalFr, useAffichagePrixTtc} from 'ff-common';
import {NatureDuPrix, Unite} from 'api-types/ffconnect';
import {TypographyProps} from '@chakra-ui/system';
import {PrixTag} from '@/domains/catalogue/components/PrixTag';
import {CategorieDeTarif} from 'api-types/tolede';
import {ModeTarifaireSouhaite} from 'api-types/ffconnect';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {PrixEditable} from '@/domains/panier/PrixEditor/PrixEditable';
import {PrixPostPromotionTooltip} from '@/domains/panier/PrixEditor/PrixPostPromotionTooltip';
import {prixAfficheEstPromo} from '@/domains/panier/PrixEditor/prix-affiche-est-promo';
import {usePeutModifierPrix} from '@/hooks/auth/usePermissionsCommerciales';
import {AjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {EditionPrixState} from '@/hooks/panier/usePriceEditor';
import {useClient} from '@/haligator-factory';

//Utilisé dans le cadencier, où on veut mettre en avant le prix unitaire
export const PrixBlockUnitaire: React.FC<{
  prixUnitaire: number;
  /** Prix unitaire TTC calculé côté serveur ; requis pour l'affichage TTC client. */
  prixUnitaireTtc?: number;
  uniteFacturation: Unite;
  tauxTVA: number | undefined;
  style?: StackProps;
  typePrix?: NatureDuPrix;
  categorieDeTarif?: CategorieDeTarif;
  modeTarifaireSouhaite?: ModeTarifaireSouhaite;
  fontSizeMain?: TypographyProps['fontSize'];
  fontSizeSecondary?: TypographyProps['fontSize'];
  ligneDePanier?: State<LigneDePanierDesc>;
  /**
   * Écran où le bloc est rendu, à passer dès qu'une `ligneDePanier` est fournie : il décide
   * du prix affiché (cf. `PrixEditable`). Par défaut `'catalogue'`, le cas des écrans qui
   * montrent le prix du catalogue même quand l'article est déjà au panier.
   */
  contexte?: 'panier' | 'catalogue';
  /** Cadencier hors panier (hors précommande) : crayon + `ajouterAvecPrixSaisi`. */
  saisieAvantAjout?: boolean;
  ajouterAvecPrixSaisi?: AjouterAvecPrixSaisi;
  /** Requis hors panier pour la modale prix métier (`configuration-prix-article`). */
  codeArticle?: string;
  /** Remonte le draft au parent pour le preview des marges. */
  onEditionPrixChange?: (state: EditionPrixState) => void;
  /** Prix affiché en promo, sans passer par le tag (la carte le rend déjà). */
  estPromo?: boolean;
}> = ({
  prixUnitaire,
  prixUnitaireTtc,
  uniteFacturation,
  tauxTVA,
  style,
  typePrix,
  categorieDeTarif,
  modeTarifaireSouhaite,
  fontSizeMain = 'lg',
  fontSizeSecondary = 'xs',
  ligneDePanier,
  contexte = 'catalogue',
  saisieAvantAjout = false,
  ajouterAvecPrixSaisi,
  codeArticle,
  onEditionPrixChange,
  estPromo = false,
}) => {
  const peutModifierPrix = usePeutModifierPrix();
  const promo = estPromo || prixAfficheEstPromo(typePrix, categorieDeTarif);
  const prixMarche =
    typePrix === 'marche' ||
    categorieDeTarif === 'M' ||
    categorieDeTarif === 'N' ||
    categorieDeTarif === 'S';
  const peutSaisirAvantAjout =
    saisieAvantAjout && !ligneDePanier && peutModifierPrix && !prixMarche;

  if (ligneDePanier || peutSaisirAvantAjout) {
    // Mode édition du prix (commercial) : affiche le composant d'édition inline du prix avec les mêmes infos que le mode lecture seule
    // + possibilité de modifier le prix si l'utilisateur a la permission `changer-le-prix`.
    return (
      <PrixEditable
        ligneDePanier={ligneDePanier}
        prixUnitaire={prixUnitaire}
        prixUnitaireTtc={prixUnitaireTtc}
        contexte={contexte}
        uniteFacturation={uniteFacturation}
        tauxTVA={tauxTVA}
        typePrix={typePrix}
        categorieDeTarif={categorieDeTarif}
        modeTarifaireSouhaite={modeTarifaireSouhaite}
        fontSizeMain={fontSizeMain}
        fontSizeSecondary={fontSizeSecondary}
        style={style}
        saisieAvantAjout={peutSaisirAvantAjout}
        ajouterAvecPrixSaisi={
          peutSaisirAvantAjout ? ajouterAvecPrixSaisi : undefined
        }
        codeArticle={codeArticle}
        onEditionPrixChange={onEditionPrixChange}
        estPromo={promo}
      />
    );
  }
  // Affichage en lecture seule (non commercial) : prix unitaire + tag de catégorie de prix si pertinent
  return (
    <PrixBlockUnitaireReadOnly
      prixUnitaire={prixUnitaire}
      prixUnitaireTtc={prixUnitaireTtc}
      uniteFacturation={uniteFacturation}
      tauxTVA={tauxTVA}
      typePrix={typePrix}
      categorieDeTarif={categorieDeTarif}
      modeTarifaireSouhaite={modeTarifaireSouhaite}
      fontSizeMain={fontSizeMain}
      fontSizeSecondary={fontSizeSecondary}
      style={style}
      codeArticle={codeArticle}
      estPromo={promo}
    />
  );
};

const PrixBlockUnitaireReadOnly: React.FC<{
  prixUnitaire: number;
  prixUnitaireTtc?: number;
  uniteFacturation: Unite;
  tauxTVA: number | undefined;
  style?: StackProps;
  typePrix?: NatureDuPrix;
  categorieDeTarif?: CategorieDeTarif;
  modeTarifaireSouhaite?: ModeTarifaireSouhaite;
  fontSizeMain?: TypographyProps['fontSize'];
  fontSizeSecondary?: TypographyProps['fontSize'];
  codeArticle?: string;
  estPromo?: boolean;
}> = ({
  prixUnitaire,
  prixUnitaireTtc,
  uniteFacturation,
  tauxTVA,
  style,
  typePrix,
  categorieDeTarif,
  modeTarifaireSouhaite,
  fontSizeMain = 'lg',
  fontSizeSecondary = 'xs',
  codeArticle,
  estPromo = false,
}) => {
  const {t} = useTranslation();
  const peutModifierPrix = usePeutModifierPrix();
  const affichagePrixTtc = useAffichagePrixTtc(useClient());
  const afficherTtc = affichagePrixTtc && prixUnitaireTtc !== undefined;
  const prixAffiche = afficherTtc ? prixUnitaireTtc : prixUnitaire;
  const labelTaxe = afficherTtc
    ? t('ligne_produit.TTC')
    : t('ligne_produit.HT');
  // Un tag n'est réellement rendu que pour 'promo'/'marche' (pas pour 'standard') ou si categorieDeTarif/modeTarifaireSouhaite est défini.
  const typePrixRendTag = typePrix === 'promo' || typePrix === 'marche';
  const hasTag =
    !!(categorieDeTarif ?? modeTarifaireSouhaite) || typePrixRendTag;
  const blocPrix = (
    <HStack
      gap='0.5'
      h='30px'
      alignItems='center'
      width='100%'
    >
      <Text
        textColor='black.text'
        fontSize={fontSizeMain}
        fontWeight='700'
      >
        {toDecimalFr(prixAffiche, 2, 3)}
      </Text>
      <Text>€</Text>
      <Text>
        {labelTaxe}/{t(`unite.${uniteFacturation}`, {count: 1})}
      </Text>
      {peutModifierPrix && (
        // Spacer invisible : aligne ce bloc avec les blocs PrixEditable (au panier)
        // qui réservent la place pour les icônes Edit + Reset.
        <HStack
          ml='auto'
          spacing={0}
          alignItems='center'
          visibility='hidden'
        >
          <Box
            display='flex'
            alignItems='center'
          >
            <EditIcon boxSize={4} />
          </Box>
          <Box
            mr={1}
            w='20px'
            h='20px'
          />
        </HStack>
      )}
    </HStack>
  );

  return (
    <VStack
      minW={peutModifierPrix ? '10.5rem' : '11em'}
      alignItems='start'
      gap='0'
      mt={{base: '15px', md: hasTag ? '-12px' : '15px'}}
      fontSize={fontSizeSecondary}
      {...style}
    >
      {hasTag && (
        <PrixTag
          typePrix={typePrix}
          categorieDeTarif={categorieDeTarif}
          modeTarifaireSouhaite={modeTarifaireSouhaite}
          styleProps={{mb: 1}}
        />
      )}
      {/* Hors promo, ou sans code article, le tooltip ne peut pas s'afficher. */}
      {estPromo && codeArticle ? (
        <PrixPostPromotionTooltip
          codeArticle={codeArticle}
          estPromo
          shouldShow
        >
          {blocPrix}
        </PrixPostPromotionTooltip>
      ) : (
        blocPrix
      )}
      {!afficherTtc && tauxTVA !== undefined && (
        <Text color='gray.dark'>
          {t('ligne_produit.tva_percent', {tauxTVA})}
        </Text>
      )}
    </VStack>
  );
};
